import { useContext, useState, useCallback } from "react";
import { toast } from "react-toastify";
import { GlobalContext } from "../../Context/GlobalContext";

const API = {
  LIST: "/Offer/v1.0/getSpecialOffer",
  ADD: "/Offer/v1.0/insertSpecialOffer",
  ASSIGN: "/Offer/v1.0/insertCustomerSpecialOffer",
  DEL: "/Offer/v1.0/deleteSpecialOffer",
  CUSTOMER_LIST: "/Offer/v1.0/getCustomerSpecialOffer",
};

export default function useSpecialOffer() {
  const { api, userID } = useContext(GlobalContext);

  const [offers, setOffers] = useState([]);
  const [customerOffers, setCustomerOffers] = useState([]);
  const [loading, setLoading] = useState(false);

  const postRequest = useCallback(
    async (endpoint, payload) => {
      const response = await api.post(
        endpoint,
        payload
      );

      return response?.data;
    },
    [api]
  );

  /* ---------------- FETCH OFFERS ---------------- */

  const fetchOffers = async () => {
    if (!userID) {
      toast.error("Missing userid");
      return;
    }

    try {
      setLoading(true);

      const data = await postRequest(
        API.LIST,
        {
          userid: userID,
        }
      );

      if (data?.responseCode === "00") {
        setOffers(data?.response || []);
      } else {
        setOffers([]);
      }

      return data;
    } catch (err) {
      console.error(err);
      toast.error("Failed to load offers");
      return null;
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- FETCH CUSTOMER COUPONS ---------------- */

  const fetchCustomerOffers = async (
    mobile = ""
  ) => {
    try {
      setLoading(true);

      const data = await postRequest(
        API.CUSTOMER_LIST,
        {
          userid: userID,
          mobileNumber: mobile,
        }
      );

      if (data?.responseCode === "00") {
        setCustomerOffers(
          data?.response || []
        );
      } else {
        setCustomerOffers([]);
      }

      return data;
    } catch (err) {
      console.error(err);
      toast.error(
        "Failed to fetch customer coupons"
      );

      return null;
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- ADD OFFER ---------------- */

  const addOffer = async (offerData) => {
    try {
      setLoading(true);

      const data = await postRequest(
        API.ADD,
        {
          userid: userID,
          ...offerData,
        }
      );

      if (data?.responseCode === "00") {
        toast.success(
          "Offer created successfully"
        );

        await fetchOffers();
      } else {
        toast.error(
          data?.responseMessage ||
            "Failed to create offer"
        );
      }

      return data;
    } catch (err) {
      console.error(err);
      toast.error("API Error");

      return null;
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- DELETE OFFER ---------------- */

  const deleteOffer = async (id) => {
    try {
      setLoading(true);

      const data = await postRequest(
        API.DEL,
        { id }
      );

      if (data?.responseCode === "00") {
        toast.success("Offer deleted");

        await fetchOffers();
      } else {
        toast.error(
          data?.responseMessage
        );
      }

      return data;
    } catch (err) {
      console.error(err);
      toast.error("Delete failed");

      return null;
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- ASSIGN OFFER ---------------- */

  const assignOffer = async (
    offerid,
    mobileNumber
  ) => {
    try {
      setLoading(true);

      const data = await postRequest(
        API.ASSIGN,
        {
          userid: userID,
          offerid,
          mobileNumber,
        }
      );

      if (data?.responseCode === "00") {
        toast.success(
          "Offer assigned successfully"
        );

        await fetchCustomerOffers(
          mobileNumber
        );
      } else {
        toast.error(
          data?.responseMessage
        );
      }

      return data;
    } catch (err) {
      console.error(err);
      toast.error("Assignment failed");

      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    offers,
    customerOffers,
    loading,
    fetchOffers,
    fetchCustomerOffers,
    addOffer,
    deleteOffer,
    assignOffer,
  };
}